import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Space, Tooltip, message, Alert } from 'antd';
import { CodeOutlined, EyeOutlined } from '@ant-design/icons';

import { uploadImage } from '../utils/caminoUploads';

// Editor compartido para EtapaEditor y MochilaEditor.
// Añade paleta de color y background, inserción de imagen (sube a Bunny) y
// un toggle vista visual / vista código para editar HTML crudo (útil para
// diseñar bloques con flex, columnas, etc.).
const CaminoRichEditor = ({ value = '', onChange, placeholder }) => {
  const [mode, setMode] = useState('visual');
  const quillRef = useRef(null);

  const quillImageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        message.error('La imagen excede 10 MB');
        return;
      }
      try {
        message.loading({ content: 'Subiendo imagen…', key: 'qimg', duration: 0 });
        const url = await uploadImage(file);
        const editor = quillRef.current?.getEditor?.();
        if (editor) {
          const range = editor.getSelection(true) || { index: editor.getLength() };
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1);
        }
        message.success({ content: 'Imagen insertada', key: 'qimg' });
      } catch (error) {
        console.error(error);
        message.error({ content: 'Error subiendo imagen', key: 'qimg' });
      }
    };
    input.click();
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'link', 'image'],
        ['clean']
      ],
      handlers: { image: quillImageHandler }
    }
  }), [quillImageHandler]);

  const quillFormats = useMemo(() => ([
    'header',
    'bold', 'italic', 'underline',
    'color', 'background',
    'list', 'bullet',
    'align',
    'blockquote', 'link', 'image'
  ]), []);

  const handleToggle = () => {
    setMode(m => (m === 'visual' ? 'code' : 'visual'));
  };

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Tooltip title={mode === 'visual'
            ? 'Editar HTML crudo (útil para columnas, flex, etc.)'
            : 'Volver a vista visual'}>
            <Button
              size="small"
              icon={mode === 'visual' ? <CodeOutlined /> : <EyeOutlined />}
              onClick={handleToggle}
            >
              {mode === 'visual' ? 'Ver código' : 'Vista visual'}
            </Button>
          </Tooltip>
        </Space>
      </div>

      {mode === 'visual' ? (
        <div className="camino-quill-wrap">
          <style>{`.camino-quill-wrap .ql-editor { min-height: 320px; }`}</style>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            modules={quillModules}
            formats={quillFormats}
            style={{ background: 'white' }}
          />
        </div>
      ) : (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message="Editando HTML directo"
            description={'Puedes usar cualquier HTML/CSS inline: <div style="display:flex;gap:12px">…</div>, <div style="columns:2">…</div>, etc. Al volver a vista visual algunos estilos pueden reagruparse.'}
          />
          <textarea
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            style={{
              width: '100%',
              minHeight: 280,
              padding: 12,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.5,
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              resize: 'vertical',
              background: '#fafafa'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CaminoRichEditor;
